import { useEffect } from 'react';

const SITE_NAME = 'Keystone by CloudVoro';
const SITE_URL = 'https://keystone.cloudvoro.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og/default.png`;

/**
 * resolveOgImage, return a per-page OG image URL by convention.
 *   "/cheese-production-software"  → "/og/cheese-production-software.png"
 *   "/case-study/cashel-blue-story" → "/og/case-study__cashel-blue-story.png"
 *   "/" or ""                      → "/og/default.png"
 * The static-route generator follows the exact same slug rule.
 */
function resolveOgImage(path) {
  if (!path || path === '/') return DEFAULT_OG_IMAGE;
  const slug = path.replace(/^\//, '').replace(/\//g, '__');
  return `${SITE_URL}/og/${slug}.png`;
}

/**
 * setMeta, idempotent helper. Updates an existing tag in <head> or
 * inserts one tagged with `data-seo="1"` so we can clean up on unmount.
 */
function setMeta(attr, key, content, owned) {
  if (content == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
    el.setAttribute('data-seo', '1');
  }
  // Remember the previous content so we can restore on unmount
  if (!created && !owned.has(el)) {
    owned.set(el, { attr, key, prev: el.getAttribute('content') });
  } else if (created) {
    owned.set(el, { attr, key, prev: null, created: true });
  }
  el.setAttribute('content', content);
  return el;
}

function setLink(rel, href, owned) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created = true;
  }
  if (!owned.has(el)) {
    owned.set(el, { kind: 'link', rel, prev: el.getAttribute('href'), created });
  }
  el.setAttribute('href', href);
  return el;
}

function setStructuredData(jsonLd, owned) {
  if (!jsonLd) return null;
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.setAttribute('data-seo-jsonld', '1');
  el.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(el);
  owned.set(el, { kind: 'script', created: true });
  return el;
}

/**
 * SEO, declarative head manager (no deps).
 *
 * Usage:
 *   <SEO
 *     title="Pricing, Transparent monthly licence"
 *     description="…"
 *     path="/site/pricing"
 *     keywords="cheese ERP, food traceability, dispatch"
 *     jsonLd={{ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Pricing' }}
 *   />
 */
export default function SEO({
  title,
  description,
  path = '',
  image,
  keywords = '',
  type = 'website',
  noindex = false,
  jsonLd = null,
  lang = 'en',
  alternates = null,
}) {
  useEffect(() => {
    const owned = new Map();
    // Don't auto-append site name; route titles already include brand context
    // where useful. Keeps total title length within Google's ~60-char limit.
    const fullTitle = title || SITE_NAME;
    const url = path ? `${SITE_URL}${path}` : SITE_URL;
    const desc = description || '';
    const ogImage = image || resolveOgImage(path);

    // Title
    const prevTitle = document.title;
    document.title = fullTitle;

    // Standard meta
    setMeta('name', 'description', desc, owned);
    if (keywords) setMeta('name', 'keywords', keywords, owned);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large', owned);
    setMeta('name', 'author', SITE_NAME, owned);

    // Open Graph
    setMeta('property', 'og:title', fullTitle, owned);
    setMeta('property', 'og:description', desc, owned);
    setMeta('property', 'og:url', url, owned);
    setMeta('property', 'og:type', type, owned);
    setMeta('property', 'og:image', ogImage, owned);
    setMeta('property', 'og:image:width', '1200', owned);
    setMeta('property', 'og:image:height', '630', owned);
    setMeta('property', 'og:image:alt', fullTitle, owned);
    setMeta('property', 'og:site_name', SITE_NAME, owned);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image', owned);
    setMeta('name', 'twitter:title', fullTitle, owned);
    setMeta('name', 'twitter:description', desc, owned);
    setMeta('name', 'twitter:image', ogImage, owned);
    setMeta('name', 'twitter:image:alt', fullTitle, owned);

    // Canonical
    setLink('canonical', url, owned);

    // Language + hreflang alternates (multilingual SEO pages)
    if (lang) document.documentElement.lang = lang;
    document.querySelectorAll('link[data-seo-alt="1"]').forEach((el) => el.remove());
    (alternates || []).forEach((a) => {
      const el = document.createElement('link');
      el.rel = 'alternate';
      el.hreflang = a.hreflang;
      el.href = a.href;
      el.setAttribute('data-seo-alt', '1');
      document.head.appendChild(el);
    });

    // JSON-LD structured data, accept a single object OR an array of objects.
    //
    // The static-HTML pre-renderer (scripts/generate-static-routes.js) ships
    // a full set of JSON-LD blocks tagged `data-prerender-jsonld="1"` so
    // non-JS crawlers always see structured data. When React hydrates we
    // must REMOVE those pre-rendered blocks before injecting our own,
    // otherwise the live DOM carries two copies of every schema (Org,
    // FAQPage, BreadcrumbList, SoftwareApplication…) and Google Search
    // Console flags critical "Duplicate field 'FAQPage'" errors that block
    // the page from rich-result eligibility.
    document
      .querySelectorAll('script[type="application/ld+json"][data-prerender-jsonld]')
      .forEach((el) => el.remove());

    const jsonLdEls = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        if (item) {
          const el = setStructuredData(item, owned);
          if (el) jsonLdEls.push(el);
        }
      });
    }

    return () => {
      document.title = prevTitle;
      // Restore mutated tags or remove the ones we created
      owned.forEach((info, el) => {
        if (info.created) {
          el.remove();
        } else if (info.kind === 'link') {
          if (info.prev != null) el.setAttribute('href', info.prev);
        } else if (info.prev != null) {
          el.setAttribute('content', info.prev);
        }
      });
      jsonLdEls.forEach((el) => el.remove());
    };
  }, [title, description, path, image, keywords, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}
